// src/pages/Clientes.js
//
// Gestión de clientes de la empresa.
//   - Tabla filtrable con búsqueda instantánea.
//   - Chips por estado (activo/inactivo, con crédito, moroso, saldo pendiente).
//   - Botón "Nuevo cliente" abre EditorCliente.
//   - Icono editar por fila → EditorCliente en modo editar.
//   - Icono $ para abonar al saldo (solo si tiene saldo > 0).
//   - Icono historial → dialog con historial de compras.
//   - Botón "Grupos" abre sub-página.

import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Card, Typography, Stack, Button, IconButton, Chip, Alert, Tooltip,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
  TextField, MenuItem, CircularProgress, Dialog, DialogTitle, DialogContent,
  DialogActions, InputAdornment,
} from '@mui/material';
import PeopleIcon         from '@mui/icons-material/People';
import PersonAddIcon      from '@mui/icons-material/PersonAdd';
import EditIcon           from '@mui/icons-material/Edit';
import DeleteIcon         from '@mui/icons-material/DeleteOutline';
import AttachMoneyIcon    from '@mui/icons-material/AttachMoney';
import HistoryIcon        from '@mui/icons-material/History';
import RefreshIcon        from '@mui/icons-material/Refresh';
import GroupWorkIcon      from '@mui/icons-material/GroupWork';
import WarningIcon        from '@mui/icons-material/Warning';
import { useApp } from '../context/AppContext';
import { clientes as apiClientes, gruposCliente as apiGrupos } from '../api/posApi';
import EditorCliente from '../components/EditorCliente';

const fmtCOP = (n) => (Number(n) || 0).toLocaleString('es-CO',
  { style: 'currency', currency: 'COP', maximumFractionDigits: 0 });
const fmtFecha = (s) => {
  if (!s) return '—';
  try { return new Date(s).toLocaleDateString('es-CO'); } catch { return s; }
};

const Clientes = () => {
  const { empresa, notify } = useApp();
  const empresaId = empresa?.empresaId || empresa?.EmpresaId;

  const [filtros, setFiltros]     = useState({ q: '', soloConCredito: false, soloMorosos: false, grupoId: '' });
  const [datos, setDatos]         = useState([]);
  const [total, setTotal]         = useState(0);
  const [grupos, setGrupos]       = useState([]);
  const [cargando, setCargando]   = useState(false);
  const [dialog, setDialog]       = useState(null);
  const [gruposAbierto, setGruposAbierto] = useState(false);

  const cargarGrupos = useCallback(async () => {
    if (!empresaId) return;
    try {
      const g = await apiGrupos.listar(empresaId);
      setGrupos(Array.isArray(g) ? g : []);
    } catch { /* silent */ }
  }, [empresaId]);

  const cargar = useCallback(async () => {
    if (!empresaId) return;
    setCargando(true);
    try {
      const r = await apiClientes.listar(empresaId, {
        q: filtros.q || undefined,
        soloConCredito: filtros.soloConCredito || undefined,
        soloMorosos: filtros.soloMorosos || undefined,
        grupoId: filtros.grupoId || undefined,
        soloActivos: true, page: 1, pageSize: 100,
      });
      setDatos(r.data || []);
      setTotal(r.total || 0);
    } catch (e) {
      notify(e.mensajeUsuario || 'No se pudo cargar clientes.', 'error');
    } finally { setCargando(false); }
  }, [empresaId, filtros, notify]);

  useEffect(() => { cargar(); cargarGrupos(); }, [cargar, cargarGrupos]);

  const editar = async (c) => {
    try {
      const det = await apiClientes.obtener(empresaId, c.clienteId);
      setDialog({ tipo: 'editar', cliente: det });
    } catch (e) { notify(e.mensajeUsuario || 'No se pudo cargar el cliente.', 'error'); }
  };

  const eliminar = async (c) => {
    if (c.esConsumidorFinal) return;
    if (!window.confirm(`¿Eliminar cliente "${c.nombreCompleto}"?`)) return;
    try {
      await apiClientes.eliminar(empresaId, c.clienteId);
      await cargar();
      notify('Cliente eliminado.', 'success');
    } catch (e) { notify(e.mensajeUsuario || 'No se pudo eliminar.', 'error'); }
  };

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 3 }}>
        <PeopleIcon color="primary" fontSize="large" />
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h4" fontWeight={700}>Clientes</Typography>
          <Typography variant="body2" color="text.secondary">
            Base de clientes de la empresa, con crédito y fiado
          </Typography>
        </Box>
        <Button variant="outlined" startIcon={<GroupWorkIcon />}
                onClick={() => setGruposAbierto(true)}>
          Grupos
        </Button>
        <Button variant="contained" startIcon={<PersonAddIcon />}
                onClick={() => setDialog({ tipo: 'crear' })}>
          Nuevo cliente
        </Button>
      </Stack>

      <Card sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap' }}>
          <TextField size="small" placeholder="Buscar por nombre, documento, teléfono, email"
                     value={filtros.q}
                     onChange={(e) => setFiltros(f => ({ ...f, q: e.target.value }))}
                     sx={{ minWidth: 320, flexGrow: 1 }} />
          <TextField select size="small" label="Grupo" sx={{ minWidth: 180 }}
                     value={filtros.grupoId}
                     onChange={(e) => setFiltros(f => ({ ...f, grupoId: e.target.value }))}>
            <MenuItem value="">Todos</MenuItem>
            {grupos.map(g => (
              <MenuItem key={g.grupoClienteId} value={g.grupoClienteId}>{g.nombre}</MenuItem>
            ))}
          </TextField>
          <TextField select size="small" label="Con crédito" sx={{ minWidth: 130 }}
                     value={filtros.soloConCredito ? '1' : '0'}
                     onChange={(e) => setFiltros(f => ({ ...f, soloConCredito: e.target.value === '1' }))}>
            <MenuItem value="0">Todos</MenuItem>
            <MenuItem value="1">Con crédito</MenuItem>
          </TextField>
          <TextField select size="small" label="Morosidad" sx={{ minWidth: 130 }}
                     value={filtros.soloMorosos ? '1' : '0'}
                     onChange={(e) => setFiltros(f => ({ ...f, soloMorosos: e.target.value === '1' }))}>
            <MenuItem value="0">Todos</MenuItem>
            <MenuItem value="1">Solo morosos</MenuItem>
          </TextField>
          <IconButton onClick={cargar}><RefreshIcon /></IconButton>
        </Stack>
      </Card>

      <Card>
        {cargando ? (
          <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress /></Box>
        ) : datos.length === 0 ? (
          <Alert severity="info" sx={{ m: 2 }}>Sin clientes con estos filtros.</Alert>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Cliente</TableCell>
                  <TableCell>Documento</TableCell>
                  <TableCell>Contacto</TableCell>
                  <TableCell>Grupo</TableCell>
                  <TableCell align="right">Compras</TableCell>
                  <TableCell align="right">Saldo</TableCell>
                  <TableCell>Última compra</TableCell>
                  <TableCell align="right">Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {datos.map(c => (
                  <TableRow key={c.clienteId} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {c.nombreCompleto}
                        {c.esConsumidorFinal && (
                          <Chip size="small" label="Consumidor final" sx={{ ml: 1 }} color="default" />
                        )}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">{c.codigo}</Typography>
                    </TableCell>
                    <TableCell><Typography variant="caption">{c.numeroDocumento}</Typography></TableCell>
                    <TableCell>
                      {c.celular && <Typography variant="caption" display="block">{c.celular}</Typography>}
                      {c.email && <Typography variant="caption" color="text.secondary" display="block">{c.email}</Typography>}
                    </TableCell>
                    <TableCell>
                      {c.grupoNombre
                        ? <Chip size="small" label={c.grupoNombre} variant="outlined" />
                        : <Typography variant="caption" color="text.secondary">—</Typography>}
                    </TableCell>
                    <TableCell align="right">
                      <Stack alignItems="flex-end">
                        <Typography variant="body2">{fmtCOP(c.totalCompras)}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {c.numeroCompras} {c.numeroCompras === 1 ? 'venta' : 'ventas'}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="flex-end">
                        {c.bloqueadoPorMorosidad && (
                          <Tooltip title="Bloqueado por morosidad">
                            <WarningIcon color="error" fontSize="small" />
                          </Tooltip>
                        )}
                        <b style={{ color: c.saldoActual > 0 ? '#d32f2f' : 'inherit' }}>
                          {fmtCOP(c.saldoActual)}
                        </b>
                      </Stack>
                      {c.tieneCredito && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          Límite {fmtCOP(c.limiteCredito)}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell><Typography variant="caption">{fmtFecha(c.fechaUltimaCompra)}</Typography></TableCell>
                    <TableCell align="right">
                      <Tooltip title="Historial de compras">
                        <IconButton size="small" onClick={() => setDialog({ tipo: 'historial', cliente: c })}>
                          <HistoryIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {c.saldoActual > 0 && (
                        <Tooltip title="Abonar al saldo">
                          <IconButton size="small" color="success"
                                      onClick={() => setDialog({ tipo: 'abono', cliente: c })}>
                            <AttachMoneyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Editar">
                        <IconButton size="small" onClick={() => editar(c)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {!c.esConsumidorFinal && (
                        <Tooltip title="Eliminar">
                          <IconButton size="small" color="error" onClick={() => eliminar(c)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        {datos.length > 0 && (
          <Box sx={{ p: 1.5, textAlign: 'right' }}>
            <Typography variant="caption" color="text.secondary">
              {datos.length} de {total} clientes
            </Typography>
          </Box>
        )}
      </Card>

      {(dialog?.tipo === 'crear' || dialog?.tipo === 'editar') && (
        <EditorCliente
          modo={dialog.tipo}
          cliente={dialog.cliente}
          empresaId={empresaId}
          onClose={() => setDialog(null)}
          onSaved={async () => { setDialog(null); await cargar(); notify('Cliente guardado.', 'success'); }}
          onError={(m) => notify(m, 'error')}
        />
      )}
      {dialog?.tipo === 'abono' && (
        <DialogAbono cliente={dialog.cliente} empresaId={empresaId}
                     onClose={() => setDialog(null)}
                     onDone={async (nuevo) => {
                       setDialog(null); await cargar();
                       notify(`Abono registrado. Nuevo saldo: ${fmtCOP(nuevo)}`, 'success');
                     }}
                     onError={(m) => notify(m, 'error')} />
      )}
      {dialog?.tipo === 'historial' && (
        <DialogHistorial cliente={dialog.cliente} empresaId={empresaId}
                         onClose={() => setDialog(null)}
                         notify={notify} />
      )}
      {gruposAbierto && (
        <DialogGrupos empresaId={empresaId} onClose={() => { setGruposAbierto(false); cargarGrupos(); }}
                      notify={notify} />
      )}
    </Box>
  );
};

// ══════════════════════════════════════════════════════════════════════
// Abono
// ══════════════════════════════════════════════════════════════════════

function DialogAbono({ cliente, empresaId, onClose, onDone, onError }) {
  const [monto, setMonto] = useState('');
  const [notas, setNotas] = useState('');
  const [g, setG] = useState(false);
  const submit = async () => {
    setG(true);
    try {
      const r = await apiClientes.abonar(empresaId, cliente.clienteId,
        parseFloat(monto) || 0, notas || null);
      onDone(r.saldoActual);
    } catch (e) { onError(e.mensajeUsuario || 'No se pudo registrar el abono.'); }
    finally { setG(false); }
  };
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>Abonar al saldo</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" sx={{ mb: 2 }}>
          <b>{cliente.nombreCompleto}</b><br />
          Saldo pendiente: <b style={{ color: '#d32f2f' }}>{fmtCOP(cliente.saldoActual)}</b>
        </Typography>
        <TextField fullWidth required autoFocus type="number" label="Monto del abono"
                   value={monto} onChange={(e) => setMonto(e.target.value)}
                   InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                   sx={{ mb: 2 }} />
        <TextField fullWidth multiline rows={2} label="Notas (opcional)"
                   value={notas} onChange={(e) => setNotas(e.target.value)} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={g}>Cancelar</Button>
        <Button variant="contained" color="success" onClick={submit}
                disabled={!monto || g || parseFloat(monto) <= 0 || parseFloat(monto) > cliente.saldoActual}>
          {g ? 'Registrando…' : 'Registrar abono'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ══════════════════════════════════════════════════════════════════════
// Historial de compras
// ══════════════════════════════════════════════════════════════════════

function DialogHistorial({ cliente, empresaId, onClose, notify }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalCompras, setTotalCompras] = useState(0);
  const [cargando, setCargando] = useState(false);

  useEffect(() => {
    (async () => {
      setCargando(true);
      try {
        const r = await apiClientes.historial(empresaId, cliente.clienteId, 1, 100);
        setItems(r.data || []);
        setTotal(r.total || 0);
        setTotalCompras(r.totalCompras || 0);
      } catch (e) { notify(e.mensajeUsuario || 'No se pudo cargar.', 'error'); }
      finally { setCargando(false); }
    })();
  }, [empresaId, cliente.clienteId, notify]);

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>
        Historial de compras: {cliente.nombreCompleto}
        <Typography variant="caption" display="block" color="text.secondary">
          {total} ventas · Total {fmtCOP(totalCompras)}
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        {cargando ? (
          <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress /></Box>
        ) : items.length === 0 ? (
          <Alert severity="info">Sin compras registradas.</Alert>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Fecha</TableCell>
                  <TableCell>Ticket</TableCell>
                  <TableCell>Sucursal</TableCell>
                  <TableCell>Estado</TableCell>
                  <TableCell>Pago</TableCell>
                  <TableCell align="right">Total</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map(v => (
                  <TableRow key={v.ventaId}>
                    <TableCell><Typography variant="caption">{fmtFecha(v.fechaVenta)}</Typography></TableCell>
                    <TableCell><Typography variant="body2">{v.numeroTicket}</Typography></TableCell>
                    <TableCell><Typography variant="caption">{v.sucursalNombre || '—'}</Typography></TableCell>
                    <TableCell>
                      <Chip size="small" label={v.estado}
                            color={v.estado === 'ANULADA' ? 'error' : 'default'} variant="outlined" />
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={v.estadoPago}
                            color={v.estadoPago === 'PAGADO' ? 'success'
                                 : v.estadoPago === 'PENDIENTE' ? 'warning' : 'default'} />
                    </TableCell>
                    <TableCell align="right"><b>{fmtCOP(v.total)}</b></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cerrar</Button>
      </DialogActions>
    </Dialog>
  );
}

// ══════════════════════════════════════════════════════════════════════
// Grupos
// ══════════════════════════════════════════════════════════════════════

function DialogGrupos({ empresaId, onClose, notify }) {
  const [grupos, setGrupos] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [editando, setEditando] = useState(null);
  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const r = await apiGrupos.listar(empresaId);
      setGrupos(Array.isArray(r) ? r : []);
    } catch { /* silent */ }
    finally { setCargando(false); }
  }, [empresaId]);
  useEffect(() => { cargar(); }, [cargar]);

  const eliminar = async (g) => {
    if (!window.confirm(`¿Eliminar grupo "${g.nombre}"?`)) return;
    try {
      await apiGrupos.eliminar(empresaId, g.grupoClienteId);
      await cargar();
      notify('Grupo eliminado.', 'success');
    } catch (e) { notify(e.mensajeUsuario || 'No se pudo eliminar.', 'error'); }
  };

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>
        Grupos de cliente
        <Button variant="contained" size="small" sx={{ float: 'right' }}
                startIcon={<PersonAddIcon />}
                onClick={() => setEditando({})}>
          Nuevo grupo
        </Button>
      </DialogTitle>
      <DialogContent dividers>
        {cargando ? (
          <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress /></Box>
        ) : grupos.length === 0 ? (
          <Alert severity="info">Sin grupos creados.</Alert>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Nombre</TableCell>
                  <TableCell align="center">Descuento</TableCell>
                  <TableCell align="right">Límite crédito</TableCell>
                  <TableCell align="center">Días crédito</TableCell>
                  <TableCell align="center">Clientes</TableCell>
                  <TableCell align="right"></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {grupos.map(g => (
                  <TableRow key={g.grupoClienteId}>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>{g.nombre}</Typography>
                      {g.descripcion && <Typography variant="caption" color="text.secondary">{g.descripcion}</Typography>}
                    </TableCell>
                    <TableCell align="center">
                      {g.descuentoPorcentaje > 0 ? `${g.descuentoPorcentaje}%` : '—'}
                    </TableCell>
                    <TableCell align="right">
                      {g.limiteCreditoDefault ? fmtCOP(g.limiteCreditoDefault) : '—'}
                    </TableCell>
                    <TableCell align="center">{g.diasCreditoDefault || '—'}</TableCell>
                    <TableCell align="center">{g.cantidadClientes}</TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => setEditando(g)}><EditIcon fontSize="small" /></IconButton>
                      <IconButton size="small" color="error" onClick={() => eliminar(g)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cerrar</Button>
      </DialogActions>
      {editando && (
        <DialogEditarGrupo empresaId={empresaId} grupo={editando}
                           onClose={() => setEditando(null)}
                           onSaved={async () => { setEditando(null); await cargar(); notify('Grupo guardado.', 'success'); }}
                           onError={(m) => notify(m, 'error')} />
      )}
    </Dialog>
  );
}

function DialogEditarGrupo({ empresaId, grupo, onClose, onSaved, onError }) {
  const [f, setF] = useState({
    codigo: grupo.codigo || '',
    nombre: grupo.nombre || '',
    descripcion: grupo.descripcion || '',
    descuentoPorcentaje: grupo.descuentoPorcentaje ?? 0,
    limiteCreditoDefault: grupo.limiteCreditoDefault ?? '',
    diasCreditoDefault: grupo.diasCreditoDefault ?? 0,
    multiplicadorPuntos: grupo.multiplicadorPuntos ?? 1,
    listaPrecioId: grupo.listaPrecioId ?? null,
    color: grupo.color ?? '',
    activo: grupo.activo ?? true,
  });
  const [g, setG] = useState(false);
  const submit = async () => {
    setG(true);
    try {
      const dto = {
        ...f,
        codigo: f.codigo || undefined,
        limiteCreditoDefault: f.limiteCreditoDefault === '' ? null : Number(f.limiteCreditoDefault),
        descuentoPorcentaje: Number(f.descuentoPorcentaje) || 0,
        diasCreditoDefault: Number(f.diasCreditoDefault) || 0,
        multiplicadorPuntos: Number(f.multiplicadorPuntos) || 1,
      };
      if (grupo.grupoClienteId) await apiGrupos.actualizar(empresaId, grupo.grupoClienteId, dto);
      else await apiGrupos.crear(empresaId, dto);
      onSaved();
    } catch (e) { onError(e.mensajeUsuario || 'No se pudo guardar.'); }
    finally { setG(false); }
  };
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{grupo.grupoClienteId ? 'Editar grupo' : 'Nuevo grupo'}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField required autoFocus label="Nombre" value={f.nombre}
                     onChange={(e) => setF(s => ({ ...s, nombre: e.target.value }))} />
          <TextField label="Descripción" value={f.descripcion} multiline rows={2}
                     onChange={(e) => setF(s => ({ ...s, descripcion: e.target.value }))} />
          <Stack direction="row" spacing={2}>
            <TextField type="number" label="Descuento default"
                       value={f.descuentoPorcentaje}
                       onChange={(e) => setF(s => ({ ...s, descuentoPorcentaje: e.target.value }))}
                       InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }} />
            <TextField type="number" label="Límite crédito default"
                       value={f.limiteCreditoDefault}
                       onChange={(e) => setF(s => ({ ...s, limiteCreditoDefault: e.target.value }))}
                       InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }} />
            <TextField type="number" label="Días crédito default"
                       value={f.diasCreditoDefault}
                       onChange={(e) => setF(s => ({ ...s, diasCreditoDefault: e.target.value }))} />
          </Stack>
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
            <input type="checkbox" checked={f.activo}
                   onChange={(e) => setF(s => ({ ...s, activo: e.target.checked }))} />
            <span>Grupo activo</span>
          </label>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={g}>Cancelar</Button>
        <Button variant="contained" onClick={submit} disabled={!f.nombre || g}>
          {g ? 'Guardando…' : 'Guardar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default Clientes;
